import React, { createContext, useContext, useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SiteConfigContext = createContext(null);

// Default configuration (matches backend defaults)
const defaultConfig = {
  branding: {
    site_name: "The Jock Tax Summit",
    tagline: "Fueling Affordable Housing & Community Equity",
    logos: {
      light_mode: "/jts_logo_light.png",
      dark_mode: "/jts_logo_dark.png",
      dim_mode: "/jts_logo_dim.png",
      favicon: "/favicon.ico"
    }
  },
  event: {
    organizer_name: "Global InTech AS",
    organizer_logo: "",
    organizer_website: "https://globalintech.ai",
    event_date: "2026-04-17",
    event_time: "09:00",
    event_end_time: "18:00",
    timezone: "America/New_York",
    venue: {
      name: "Tsai Auditorium (S010)",
      address: "CGIS South, 1730 Cambridge St",
      city: "Cambridge, MA",
      country: "USA",
      map_url: "https://maps.google.com/?q=Harvard+University+CGIS+South"
    }
  },
  navigation: {
    items: [
      { id: "nav-home", label: "Home", path: "/", order: 0, visible: true },
      { id: "nav-research", label: "Research", path: "/research", order: 1, visible: true },
      { id: "nav-vernand", label: "Vernand Morency", path: "/vernand-morency", order: 2, visible: true },
      { id: "nav-speakers", label: "Speakers", path: "/speakers", order: 3, visible: true },
      { id: "nav-experience", label: "Experience", path: "/experience", order: 4, visible: true },
      { id: "nav-agenda", label: "Agenda", path: "/agenda", order: 5, visible: true },
      { id: "nav-partnership", label: "Partnership", path: "/sponsorship", order: 6, visible: true },
    ],
    show_register_button: true,
    register_button_text: "Register",
    register_button_path: "/signup"
  },
  footer: {
    about_text: "A Coalition of Influence & Impact. Reaching 500M+ people to solve the housing crisis.",
    copyright_text: "© 2026 Global InTech AS. All rights reserved.",
    contact_email: "contact@globalintech.ai",
    contact_phone: "",
    social_links: [],
    show_newsletter: false,
    newsletter_text: "Subscribe to our newsletter"
  },
  legal: {
    privacy_policy: { title: "Privacy Policy", slug: "privacy-policy", content: "" },
    terms_of_service: { title: "Terms of Service", slug: "terms-of-service", content: "" }
  },
  styling: {
    primary_color: "#C5A059",
    secondary_color: "#14213D",
    accent_color: "#D4B06A",
    heading_font: "Playfair Display",
    body_font: "Montserrat",
    border_radius: "0.75rem",
    font_scale: "1"
  }
};

export function SiteConfigProvider({ children }) {
  const [config, setConfig] = useState(defaultConfig);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deactivatedSlugs, setDeactivatedSlugs] = useState([]);

  // Fetch site configuration and deactivated pages on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [configRes, slugsRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/site-config`),
          fetch(`${BACKEND_URL}/api/pages/active-slugs`)
        ]);
        if (configRes.ok) {
          const data = await configRes.json();
          setConfig(prev => ({ ...prev, ...data }));
        }
        if (slugsRes.ok) {
          const data = await slugsRes.json();
          setDeactivatedSlugs(data.deactivated_slugs || []);
        }
      } catch (err) {
        console.error('Failed to fetch site config:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfig();
  }, []);

  // Apply global styling from site config (P2: Global Styling Controls)
  useEffect(() => {
    const styling = config.styling || defaultConfig.styling;
    if (!styling) return;

    const root = document.documentElement;

    // Apply color CSS variables via HSL conversion
    const hexToHSL = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (!result) return null;
      let r = parseInt(result[1], 16) / 255;
      let g = parseInt(result[2], 16) / 255;
      let b = parseInt(result[3], 16) / 255;
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      let h, s, l = (max + min) / 2;
      if (max === min) { h = s = 0; } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
          case g: h = ((b - r) / d + 2) / 6; break;
          case b: h = ((r - g) / d + 4) / 6; break;
          default: h = 0;
        }
      }
      return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    };

    if (styling.primary_color) {
      const hsl = hexToHSL(styling.primary_color);
      if (hsl) root.style.setProperty('--primary', hsl);
    }
    if (styling.secondary_color) {
      const hsl = hexToHSL(styling.secondary_color);
      if (hsl) root.style.setProperty('--secondary', hsl);
    }
    if (styling.accent_color) {
      const hsl = hexToHSL(styling.accent_color);
      if (hsl) root.style.setProperty('--accent', hsl);
    }
    if (styling.border_radius) {
      root.style.setProperty('--radius', styling.border_radius);
    }
    if (styling.font_scale && styling.font_scale !== '1') {
      root.style.setProperty('--font-scale', styling.font_scale);
    }

    // Apply fonts via Google Fonts
    if (styling.heading_font) {
      const id = 'site-config-heading-font';
      let link = document.getElementById(id);
      if (!link) { link = document.createElement('link'); link.id = id; link.rel = 'stylesheet'; document.head.appendChild(link); }
      link.href = `https://fonts.googleapis.com/css2?family=${styling.heading_font.replace(/ /g, '+')}:wght@300;400;500;600;700;900&display=swap`;
      root.style.setProperty('--font-heading', `'${styling.heading_font}', serif`);
    }
    if (styling.body_font) {
      const id = 'site-config-body-font';
      let link = document.getElementById(id);
      if (!link) { link = document.createElement('link'); link.id = id; link.rel = 'stylesheet'; document.head.appendChild(link); }
      link.href = `https://fonts.googleapis.com/css2?family=${styling.body_font.replace(/ /g, '+')}:wght@300;400;500;600;700&display=swap`;
      root.style.setProperty('--font-body', `'${styling.body_font}', sans-serif`);
    }
  }, [config.styling]);

  // Refresh config (can be called after CMS updates)
  const refreshConfig = async () => {
    try {
      const [configRes, slugsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/site-config`),
        fetch(`${BACKEND_URL}/api/pages/active-slugs`)
      ]);
      if (configRes.ok) {
        const data = await configRes.json();
        setConfig(prev => ({ ...prev, ...data }));
      }
      if (slugsRes.ok) {
        const slugsData = await slugsRes.json();
        setDeactivatedSlugs(slugsData.deactivated_slugs || []);
      }
      return true;
    } catch (err) {
      console.error('Failed to refresh site config:', err);
      return false;
    }
  };

  // Check if a page path is deactivated (e.g. "/signup" checks slug "signup")
  const isPageActive = (path) => {
    if (!path) return true;
    const slug = path.replace(/^\//, '');
    return !deactivatedSlugs.includes(slug);
  };

  // Check if registration/signup is active
  const isRegistrationActive = !deactivatedSlugs.includes('signup');

  // Helper to get the right logo based on theme mode
  const getLogo = (mode = 'dark') => {
    const logos = config.branding?.logos || {};
    switch (mode) {
      case 'light':
        return logos.light_mode || defaultConfig.branding.logos.light_mode;
      case 'dim':
        return logos.dim_mode || logos.dark_mode || defaultConfig.branding.logos.dim_mode;
      case 'dark':
      default:
        return logos.dark_mode || defaultConfig.branding.logos.dark_mode;
    }
  };

  // Helper to get visible navigation items sorted by order (excludes deactivated pages)
  const getNavigationItems = () => {
    const items = config.navigation?.items || defaultConfig.navigation.items;
    return items
      .filter(item => item.visible !== false)
      .filter(item => isPageActive(item.path))
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  // Helper to parse event date/time
  const getEventDateTime = () => {
    const event = config.event || defaultConfig.event;
    const dateStr = event.event_date;
    const timeStr = event.event_time || '09:00';
    
    // Parse date and time
    const [year, month, day] = dateStr.split('-').map(Number);
    const [hours, minutes] = timeStr.split(':').map(Number);
    
    return new Date(year, month - 1, day, hours, minutes);
  };

  const value = {
    config,
    isLoading,
    error,
    refreshConfig,
    getLogo,
    getNavigationItems,
    getEventDateTime,
    // Shortcut accessors
    branding: config.branding || defaultConfig.branding,
    event: config.event || defaultConfig.event,
    navigation: config.navigation || defaultConfig.navigation,
    footer: config.footer || defaultConfig.footer,
    legal: config.legal || defaultConfig.legal,
    styling: config.styling || defaultConfig.styling,
    isPageActive,
    isRegistrationActive,
    deactivatedSlugs,
  };

  return (
    <SiteConfigContext.Provider value={value}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfig() {
  const context = useContext(SiteConfigContext);
  if (!context) {
    throw new Error('useSiteConfig must be used within a SiteConfigProvider');
  }
  return context;
}

export default SiteConfigContext;
